import { ApiTask, TaskType } from "griptonite-api-client/types/tasks";
import { orderBy } from "lodash";

import { convertToInternal, getGradeValuesBetween } from "@/utils/grades";

export default function groupTasksByTypeGrade(tasks: ApiTask[]) {
  const byTypeGradeSystem = new Map<TaskType, Map<string, ApiTask[]>>();

  // result is tasks by type then by grade value
  const result = new Map<
    TaskType,
    { gradeSystem: string; gradeValue: string | null; tasks: ApiTask[] }[]
  >();

  tasks.forEach((task) => {
    if (!byTypeGradeSystem.has(task.type)) {
      byTypeGradeSystem.set(task.type, new Map());
    }

    const byGradeSystem = byTypeGradeSystem.get(task.type)!;

    let gradeSystem: string;
    if (task.route) {
      gradeSystem = task.route.gradeSystem;
    } else if (task.gradeSystem) {
      gradeSystem = task.gradeSystem;
    } else {
      return;
    }

    if (!byGradeSystem.has(gradeSystem)) {
      byGradeSystem.set(gradeSystem, []);
    }

    byGradeSystem.get(gradeSystem)!.push(task);
  });

  byTypeGradeSystem.forEach((byGradeSystem, type) => {
    // Find the one with the most entries
    let mostEntries = 0;
    let mostEntriesGradeSystem: string | null = null;
    byGradeSystem.forEach((tasks, gradeSystem) => {
      if (tasks.length > mostEntries) {
        mostEntries = tasks.length;
        mostEntriesGradeSystem = gradeSystem;
      }
    });

    byGradeSystem.forEach((tasks, gradeSystem) => {
      const byGradeValues = getMapByGradeValues(
        gradeSystem,
        tasks,
        gradeSystem === mostEntriesGradeSystem,
      );
      byGradeValues.forEach((tasks, gradeValue) => {
        if (!result.has(type)) {
          result.set(type, []);
        }
        if (gradeValue !== null) {
          result.get(type)!.push({ gradeSystem, gradeValue, tasks });
        } else {
          // add to existing if it exists
          const existing = result.get(type)!.find((entry) => entry.gradeValue === null);
          if (existing) {
            existing.tasks.push(...tasks);
          } else {
            result.get(type)!.push({ gradeSystem, gradeValue, tasks });
          }
        }
      });
      // move the null grade value to the end
      const nullGradeValue = result.get(type)!.find((entry) => entry.gradeValue === null);
      if (nullGradeValue) {
        result.get(type)!.splice(result.get(type)!.indexOf(nullGradeValue), 1);
        result.get(type)!.push(nullGradeValue);
      }
    });
  });

  return result;
}

const getMapByGradeValues = (gradeSystem: string | null, tasks: ApiTask[], expand: boolean) => {
  const result = new Map<string | null, ApiTask[]>();
  const sortedTasks = orderBy(
    tasks,
    (task) =>
      task.route?.gradeInternal ??
      (gradeSystem &&
        convertToInternal((task.route?.gradeValue || task.gradeValue) ?? null, gradeSystem)) ??
      100000,
  );

  sortedTasks.forEach((task) => {
    let gradeValue: string | null = null;
    if (task.route) {
      gradeValue = task.route.gradeValue;
    } else if (task.gradeSystem) {
      gradeValue = task.gradeValue;
    }

    const key = gradeValue;

    if (!result.has(key)) {
      result.set(key, []);
    }

    result.get(key)!.push(task);
  });

  if (expand) {
    const lastTask = sortedTasks[sortedTasks.length - 1];
    const penultimateTask = sortedTasks[sortedTasks.length - 2];
    const startGrade = sortedTasks[0].route?.gradeValue || sortedTasks[0].gradeValue;
    let endGrade = lastTask.route?.gradeValue || lastTask.gradeValue;

    if (endGrade === null && sortedTasks.length > 1) {
      endGrade = penultimateTask.route?.gradeValue || penultimateTask.gradeValue;
    }
    if (startGrade === null || endGrade === null || gradeSystem === null) {
      // use the map as is
      return result;
    }
    const newMap = new Map<string | null, ApiTask[]>();
    const allGrades = getGradeValuesBetween(gradeSystem, startGrade, endGrade);
    allGrades.forEach((grade) => {
      if (!result.has(grade)) {
        newMap.set(grade, []);
      } else {
        newMap.set(grade, result.get(grade)!);
      }
    });
    newMap.set(null, result.get(null) || []);
    return newMap;
  }
  return result;
};
